/**
 * <search-tabs> — wraps the search results layout (filter sidebar + toolbar + grid).
 *
 * Progressive enhancement: the result type tabs (All / Products / Articles / Pages) are real
 * links and still work with JS off. With JS, a tab click swaps this element's contents from a
 * section render of the target URL and smooth-scrolls to the toolbar, so switching type no
 * longer throws the shopper back to the top of the page.
 *
 * The pushState payload keeps facets.js's shape ({ searchParams }) — its global popstate
 * listener reads that key, so Back after a tab switch still re-renders the grid.
 */
class SearchTabs extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', this.onClick.bind(this));
  }

  get sectionId() {
    return this.dataset.sectionId;
  }

  onClick(event) {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const link = event.target.closest('.sr-tabs a');
    if (!link || link.getAttribute('aria-current') === 'page') return;

    event.preventDefault();
    this.navigate(link.getAttribute('href'));
  }

  navigate(url) {
    if (!url || this.loading) return;

    const target = new URL(url, window.location.origin);
    const fetchUrl = new URL(target.toString());
    fetchUrl.searchParams.set('section_id', this.sectionId);

    this.loading = true;
    this.setAttribute('aria-busy', 'true');

    fetch(fetchUrl.toString())
      .then((response) => {
        if (!response.ok) throw new Error(response.status);
        return response.text();
      })
      .then((html) => {
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const layout = parsed.querySelector('search-tabs');

        // A type with no matches renders the no-results state instead of the layout —
        // there is nothing to swap in, so let the browser follow the link.
        if (!layout) {
          window.location.href = target.toString();
          return;
        }

        this.className = layout.className;
        this.innerHTML = layout.innerHTML;
        this.syncHeroCount(parsed);
        history.pushState({ searchParams: target.search.slice(1) }, '', target.toString());
        this.scrollToToolbar();
      })
      .catch(() => {
        window.location.href = target.toString();
      })
      .finally(() => {
        this.loading = false;
        this.removeAttribute('aria-busy');
      });
  }

  /** The hero's "N results found for …" line sits outside this element, so it needs its own update. */
  syncHeroCount(parsed) {
    const source = parsed.querySelector('.sr-hero__meta');
    const target = document.querySelector('.search-page-wrapper .sr-hero__meta');
    if (source && target) target.innerHTML = source.innerHTML;
  }

  scrollToToolbar() {
    const toolbar = this.querySelector('.cl-toolbar');
    if (!toolbar) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    toolbar.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
  }
}

if (!customElements.get('search-tabs')) {
  customElements.define('search-tabs', SearchTabs);
}
